import {
    Alert,
    AlertTitle,
    Box,
    Button,
    CircularProgress,
    Link,
} from '@mui/material';
import React from 'react';
import { useConnectorIdentity } from '~/hooks/auth/useConnectorIdentity';

/**
 * What a connector says about the person in front of it, before the application
 * opens.
 *
 * Authentication itself is the browser's business here — the connector asks for
 * it over the network and the browser answers — so there is no sign-in step to
 * perform. What this adds is the answer: a caller the connector will not
 * identify, and one it identifies who holds nothing, both reach the application
 * with every request refused, and an interface that opened anyway would show
 * them a patient list that is empty because it was refused rather than because
 * there are no patients.
 *
 * The application opens only on an answer. Any failure to get one — a refusal,
 * an error status, no connector at all — keeps it closed and says which.
 */
export default function ConnectorAccessGuard({
    children,
}: {
    children: React.ReactNode;
}) {
    const identity = useConnectorIdentity();

    if (identity.isLoading || identity.isAsking) {
        return (
            <Box sx={{ p: 6, textAlign: 'center' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (identity.isUnidentified) {
        return (
            <Message title="This workstation could not be identified">
                The connector asked this browser to prove who you are and did
                not get an answer it accepted. That is usually the browser
                rather than your account: it has to be configured to answer for
                this host. An administrator can repair access from the{' '}
                <Link href="/admin">administration console</Link>.
            </Message>
        );
    }

    if (identity.signInEnabled && !identity.reachesPatientData) {
        return (
            <Message title="Your account has no access to patient data">
                You have been identified, and what your account is allowed to do
                does not include opening patients. An account that administers
                the connector is deliberately not given patient access. Ask an
                administrator for the access you need, or use the{' '}
                <Link href="/admin">administration console</Link> if you manage
                access yourself.
            </Message>
        );
    }

    if (identity.holdsNothing) {
        return (
            <Message title="Your account has no access to this connector">
                You have been identified, and nobody has given your account
                anything it may do yet. That is the state a new account is in
                until an administrator puts it in a group. Ask an administrator
                for the access you need.
            </Message>
        );
    }

    if (identity.isUnreachable) {
        return (
            <Message
                title="The connector could not be reached"
                onRetry={identity.retry}
            >
                This browser got no answer from the connector. Check that the
                service is running and that this computer can reach it.
            </Message>
        );
    }

    if (identity.failedWithStatus !== undefined) {
        return (
            <Message
                title="The connector could not say who you are"
                onRetry={identity.retry}
            >
                It answered with an error (HTTP {identity.failedWithStatus}). If
                it is restarting, try again in a moment. If this persists, ask
                an administrator to check the connector&apos;s log.
            </Message>
        );
    }

    // Answered. An installation that identifies nobody admits everyone, and so
    // does one that identified the caller and found something they may do.
    return <>{children}</>;
}

function Message({
    title,
    children,
    onRetry,
}: {
    title: string;
    children: React.ReactNode;
    /** Offered where the failure may clear by itself. */
    onRetry?: () => void;
}) {
    return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
            <Alert
                severity="warning"
                sx={{ maxWidth: 620 }}
                action={
                    onRetry && (
                        <Button color="inherit" size="small" onClick={onRetry}>
                            Try again
                        </Button>
                    )
                }
            >
                <AlertTitle>{title}</AlertTitle>
                {children}
            </Alert>
        </Box>
    );
}
